// Villager archetype (MOB-2 + MOB-3 A*, M14.1 / MB.7). PURE (ENG-1).
//
// A passive townsfolk that follows its daily schedule via the A* planner (the one
// mob class that uses it, D11): it heads to its bed at night, the meeting bell at
// midday, and mills about home otherwise. It flees the nearest hostile. Trading
// itself is a player interaction (game.useEntity → trade UI), not AI. The host
// caches the bed/bell locations into e.data (block-name lookup needs the registry);
// the archetype picks the destination purely from the clock.
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext } from '../entity';
import { findPath } from '../pathfind/astar';
import { wander, moveToward, moveAway, tickSpeed } from './shared';
import { activityAt, scheduleDestination } from '../../game/schedule';

/** Distance at which a villager flees a hostile monster. */
const FLEE_RANGE = 8;

export function villager(e: Entity, def: MobDef, ctx: MobContext): void {
  const { world, sys } = ctx;
  // Remember the spawn point as home the first time it acts.
  if (e.data.homeSet !== 1) {
    e.data.homeX = Math.floor(e.x); e.data.homeY = Math.floor(e.y); e.data.homeZ = Math.floor(e.z);
    e.data.homeSet = 1;
  }

  // Flee the nearest hostile — drop any cached path so it re-plans on the way back.
  const threat = sys.nearestEntity(e, FLEE_RANGE, (o) => o.category === 'monster');
  if (threat) { moveAway(e, ctx, threat.x, threat.z, tickSpeed(def, 1.5)); e.data.hasNext = 0; return; }

  // Pick the schedule destination for the current time of day: the cached bed at
  // night / the cached bell at midday (set by the host) — else home.
  const dest = scheduleDestination(activityAt(ctx.frame.dayTime, { employed: !!def.trades, child: e.baby }));
  let tx = e.data.homeX!, ty = e.data.homeY!, tz = e.data.homeZ!;
  if (dest === 'bed' && e.data.bedSet === 1) { tx = e.data.bedX!; ty = e.data.bedY!; tz = e.data.bedZ!; }
  else if (dest === 'meet' && e.data.bellSet === 1) { tx = e.data.bellX!; ty = e.data.bellY!; tz = e.data.bellZ!; }

  if (Math.hypot(e.x - (tx + 0.5), e.z - (tz + 0.5)) < 1.4) { wander(e, ctx, def); return; }

  // Walk to the destination along an A* path, recomputing on a cadence (or when the
  // next cell is reached). The path is cached as a single next-cell in scratch data.
  e.data.repathCd = (e.data.repathCd ?? 0) - 1;
  if (e.data.repathCd <= 0 || e.data.hasNext !== 1) {
    const path = findPath(world, e, { x: tx, y: ty, z: tz }, { budget: 200, reach: 1 });
    if (path && path.length >= 2) {
      const n = path[1]!;
      e.data.nextX = n.x; e.data.nextY = n.y; e.data.nextZ = n.z; e.data.hasNext = 1; e.data.repathCd = 8;
    } else { wander(e, ctx, def); return; }
  }
  moveToward(e, ctx, e.data.nextX! + 0.5, e.data.nextZ! + 0.5, tickSpeed(def, 1));
  if (Math.hypot(e.x - (e.data.nextX! + 0.5), e.z - (e.data.nextZ! + 0.5)) < 0.4) e.data.repathCd = 0;
}
